import { useMemo } from 'react';
import { useBulkActionsReporting } from '../use-bulk-actions-reporting/use-bulk-actions-reporting';
import type { BulkActionMenuItem } from '../../providers/bulk-actions-provider/bulk-actions-provider.component';
import { isBulkActionForBaseId } from '../../providers/bulk-actions-provider/bulk-action-target-id';

/**
 * Every bulk action in a per-target family, in registration order.
 *
 * {@link useBulkAction} fetches one action by exact id, which suits the actions that exist
 * once per page. Promotion actions exist once per target, so a caller needs all of them
 * without knowing which environments a tenant has.
 *
 * Unlike useBulkAction it doesn't warn when nothing matches: no targets is an ordinary state
 * for a single-environment tenant, not a wiring mistake.
 *
 * @param baseId - The family's base id, e.g. BULK_PROMOTE_ALL_ACTION
 * @param boundArgs - Optional arguments bound to every returned action, as useBulkAction does
 */
export const useBulkActionsForBaseId = <TArgs extends unknown[]>(
  baseId: string,
  ...boundArgs: TArgs
): BulkActionMenuItem[] => {
  const { actions } = useBulkActionsReporting();

  return useMemo(
    () =>
      actions
        .filter((action) => isBulkActionForBaseId(action.id, baseId))
        .map((action) =>
          boundArgs.length === 0
            ? action
            : { ...action, action: () => action.action?.(...boundArgs) },
        ),
    // The spread in deps is intentional -- recompute when any bound arg changes. ESLint
    // can't statically verify spreads but it's functionally correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actions, baseId, ...boundArgs],
  );
};
